import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const KAIROS_SEARCH_HISTORY_KEY = 'kairos-recent-searches-v1';
const KAIROS_SEARCH_HISTORY_LIMIT = 6;

function getKairosSearchHistory() {
  try {
    const history = JSON.parse(window.localStorage.getItem(KAIROS_SEARCH_HISTORY_KEY) || '[]');
    return Array.isArray(history) ? history.filter((term) => typeof term === 'string' && term.trim()).slice(0, KAIROS_SEARCH_HISTORY_LIMIT) : [];
  } catch (error) {
    return [];
  }
}

function saveKairosSearchTerm(term) {
  const normalized = String(term || '').trim();
  if (!normalized || normalized === '*') return;

  try {
    const history = getKairosSearchHistory().filter((item) => item.toLocaleLowerCase() !== normalized.toLocaleLowerCase());
    window.localStorage.setItem(KAIROS_SEARCH_HISTORY_KEY, JSON.stringify([normalized, ...history].slice(0, KAIROS_SEARCH_HISTORY_LIMIT)));
  } catch (error) {
    // Search remains fully usable when browser storage is unavailable.
  }
}

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  connectedCallback() {
    super.connectedCallback();
    const input = this.refs.searchPageInput;
    input.form?.addEventListener('submit', () => saveKairosSearchTerm(input.value));

    const currentTerm = new URL(window.location.href).searchParams.get('q') || '';
    saveKairosSearchTerm(currentTerm);
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
